import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ShieldCheckIcon,
  FireIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

interface Collection {
  id: string;
  name: string;
  image: string;
  bannerImage: string;
  creator: {
    name: string;
    avatar: string;
    verified: boolean;
  };
  floorPrice: number;
  floorChange24h: number;
  volume24h: number;
  volumeChange24h: number;
  totalVolume: number;
  owners: number;
  items: number;
  verified: boolean;
  featured: boolean;
  curationScore: number;
}

const mockCollections: Collection[] = [
  {
    id: '1',
    name: 'Equus Dualis II',
    image: '/Equus Dualis/Equus Dualis II ) 2.png',
    bannerImage: '/Equus Dualis/Equus Dualis II ) 4.png',
    creator: { name: 'NARDIHA Arts', avatar: '/warrior mythology/warrior mythology1.jpg', verified: true },
    floorPrice: 8.2,
    floorChange24h: 12.5,
    volume24h: 156.8,
    volumeChange24h: 25.3,
    totalVolume: 4520,
    owners: 2847,
    items: 10000,
    verified: true,
    featured: true,
    curationScore: 95,
  },
  {
    id: '2',
    name: 'Warrior Mythology',
    image: '/warrior mythology/warrior mythology10.jpg',
    bannerImage: '/warrior mythology/warrior mythology20.jpg',
    creator: { name: 'NARDIHA Arts', avatar: '/warrior mythology/warrior mythology2.jpg', verified: true },
    floorPrice: 6.5,
    floorChange24h: -5.2,
    volume24h: 89.4,
    volumeChange24h: -12.1,
    totalVolume: 2890,
    owners: 1923,
    items: 5000,
    verified: true,
    featured: false,
    curationScore: 88,
  },
  {
    id: '3',
    name: 'Equus Dualis — Origins',
    image: '/Equus Dualis/Equus Dualis II ) 12.png',
    bannerImage: '/Equus Dualis/Equus Dualis II ) 18.png',
    creator: { name: 'NARDIHA Arts', avatar: '/warrior mythology/warrior mythology3.jpg', verified: true },
    floorPrice: 5.1,
    floorChange24h: 8.9,
    volume24h: 67.2,
    volumeChange24h: 45.6,
    totalVolume: 1560,
    owners: 1245,
    items: 3000,
    verified: true,
    featured: true,
    curationScore: 92,
  },
  {
    id: '4',
    name: 'Warrior Mythology — Legends',
    image: '/warrior mythology/warrior mythology30.jpg',
    bannerImage: '/warrior mythology/warrior mythology39.jpg',
    creator: { name: 'NARDIHA Arts', avatar: '/warrior mythology/warrior mythology4.jpg', verified: false },
    floorPrice: 12.0,
    floorChange24h: 3.2,
    volume24h: 234.5,
    volumeChange24h: 18.9,
    totalVolume: 7890,
    owners: 4521,
    items: 8888,
    verified: true,
    featured: false,
    curationScore: 85,
  },
  {
    id: '5',
    name: 'Equus Dualis — Rare',
    image: '/Equus Dualis/Equus Dualis II ) 26.png',
    bannerImage: '/Equus Dualis/Equus Dualis II ) 28.png',
    creator: { name: 'NARDIHA Arts', avatar: '/warrior mythology/warrior mythology5.jpg', verified: true },
    floorPrice: 35.0,
    floorChange24h: -2.1,
    volume24h: 567.8,
    volumeChange24h: 8.7,
    totalVolume: 15600,
    owners: 3456,
    items: 1111,
    verified: true,
    featured: true,
    curationScore: 98,
  },
  {
    id: '6',
    name: 'Warrior Mythology — Genesis',
    image: '/warrior mythology/warrior mythology50.jpg',
    bannerImage: '/warrior mythology/warrior mythology60.jpg',
    creator: { name: 'NARDIHA Arts', avatar: '/warrior mythology/warrior mythology6.jpg', verified: true },
    floorPrice: 2.8,
    floorChange24h: 15.6,
    volume24h: 45.2,
    volumeChange24h: 67.3,
    totalVolume: 890,
    owners: 2134,
    items: 10000,
    verified: false,
    featured: false,
    curationScore: 76,
  },
];

const CollectionsPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('volume24h');
  const [timeRange, setTimeRange] = useState('24h');

  const formatNumber = (num: number) => {
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toFixed(2);
  };

  const filteredCollections = mockCollections
    .filter((col) =>
      col.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'volume24h') return b.volume24h - a.volume24h;
      if (sortBy === 'floorPrice') return b.floorPrice - a.floorPrice;
      if (sortBy === 'floorChange24h') return b.floorChange24h - a.floorChange24h;
      if (sortBy === 'curationScore') return b.curationScore - a.curationScore;
      return 0;
    });

  return (
    <div className="min-h-screen py-8">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-gradient">Collections</span>
          </h1>
          <p className="text-gray-400 text-lg">
            Explore top NFT collections ranked by volume, floor price, and community curation
          </p>
        </motion.div>

        {/* Featured Collections */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <div className="flex items-center space-x-2 mb-6">
            <FireIcon className="h-6 w-6 text-orange-400" />
            <h2 className="text-2xl font-bold">Featured Collections</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {mockCollections
              .filter((col) => col.featured)
              .slice(0, 3)
              .map((collection, index) => (
                <Link
                  key={collection.id}
                  to={`/collections/${collection.id}`}
                  className="group relative rounded-2xl overflow-hidden"
                >
                  <div className="aspect-[16/9] relative">
                    <img
                      src={collection.bannerImage}
                      alt={collection.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/50 to-transparent" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-end space-x-4">
                      <img
                        src={collection.image}
                        alt={collection.name}
                        className="w-16 h-16 rounded-xl border-4 border-dark-900"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-semibold text-lg">{collection.name}</h3>
                          {collection.verified && (
                            <ShieldCheckIcon className="h-5 w-5 text-primary-400" />
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-sm">
                          <span className="text-gray-400">
                            Floor: <span className="text-white">{collection.floorPrice} ETH</span>
                          </span>
                          <span className={collection.floorChange24h >= 0 ? 'text-green-400' : 'text-red-400'}>
                            {collection.floorChange24h >= 0 ? '+' : ''}{collection.floorChange24h}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col md:flex-row gap-4 mb-8"
        >
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
            <input
              type="text"
              placeholder="Search collections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-dark-800/50 border border-white/10 rounded-xl focus:outline-none focus:border-primary-500"
            />
          </div>

          <div className="flex items-center space-x-2 bg-dark-800/50 border border-white/10 rounded-xl p-1">
            {['1h', '6h', '24h', '7d', '30d'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  timeRange === range
                    ? 'bg-primary-500 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {range}
              </button>
            ))}
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-3 bg-dark-800/50 border border-white/10 rounded-xl focus:outline-none focus:border-primary-500"
          >
            <option value="volume24h">Volume</option>
            <option value="floorPrice">Floor Price</option>
            <option value="floorChange24h">% Change</option>
            <option value="curationScore">Curation Score</option>
          </select>

          <div className="flex items-center space-x-1 bg-dark-800/50 border border-white/10 rounded-xl p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'table' ? 'bg-primary-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <ListBulletIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'grid' ? 'bg-primary-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <Squares2X2Icon className="h-5 w-5" />
            </button>
          </div>
        </motion.div>

        {/* Collections Table */}
        {viewMode === 'table' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">#</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Collection</th>
                    <th className="px-6 py-4 text-right text-sm font-medium text-gray-400">Floor Price</th>
                    <th className="px-6 py-4 text-right text-sm font-medium text-gray-400">Floor Change</th>
                    <th className="px-6 py-4 text-right text-sm font-medium text-gray-400">Volume ({timeRange})</th>
                    <th className="px-6 py-4 text-right text-sm font-medium text-gray-400">Total Volume</th>
                    <th className="px-6 py-4 text-right text-sm font-medium text-gray-400">Owners</th>
                    <th className="px-6 py-4 text-right text-sm font-medium text-gray-400">Items</th>
                    <th className="px-6 py-4 text-center text-sm font-medium text-gray-400">Curation</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCollections.map((collection, index) => (
                    <tr
                      key={collection.id}
                      className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    >
                      <td className="px-6 py-4 text-sm text-gray-400">{index + 1}</td>
                      <td className="px-6 py-4">
                        <Link
                          to={`/collections/${collection.id}`}
                          className="flex items-center space-x-3 group"
                        >
                          <img
                            src={collection.image}
                            alt={collection.name}
                            className="w-12 h-12 rounded-xl object-cover"
                          />
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="font-semibold group-hover:text-primary-400 transition-colors">
                                {collection.name}
                              </span>
                              {collection.verified && (
                                <ShieldCheckIcon className="h-4 w-4 text-primary-400" />
                              )}
                            </div>
                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                              <span>by {collection.creator.name}</span>
                              {collection.creator.verified && (
                                <SparklesIcon className="h-3 w-3 text-primary-400" />
                              )}
                            </div>
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-right font-semibold">{collection.floorPrice} ETH</td>
                      <td className={`px-6 py-4 text-right ${
                        collection.floorChange24h >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        <div className="flex items-center justify-end space-x-1">
                          {collection.floorChange24h >= 0 ? (
                            <ArrowTrendingUpIcon className="h-4 w-4" />
                          ) : (
                            <ArrowTrendingDownIcon className="h-4 w-4" />
                          )}
                          <span>{Math.abs(collection.floorChange24h)}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div>
                          <span className="font-semibold">{formatNumber(collection.volume24h)} ETH</span>
                          <span className={`ml-2 text-xs ${
                            collection.volumeChange24h >= 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {collection.volumeChange24h >= 0 ? '+' : ''}{collection.volumeChange24h}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right text-gray-400">
                        {formatNumber(collection.totalVolume)} ETH
                      </td>
                      <td className="px-6 py-4 text-right text-gray-400">
                        {formatNumber(collection.owners)}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-400">
                        {formatNumber(collection.items)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center">
                          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                            collection.curationScore >= 90
                              ? 'bg-green-500/20 text-green-400'
                              : collection.curationScore >= 80
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {collection.curationScore}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Collections Grid */}
        {viewMode === 'grid' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredCollections.map((collection, index) => (
              <Link
                key={collection.id}
                to={`/collections/${collection.id}`}
                className="glass-card overflow-hidden group hover:border-primary-500/50 transition-all duration-300"
              >
                <div className="relative h-32">
                  <img
                    src={collection.bannerImage}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-800 to-transparent" />
                  <img
                    src={collection.image}
                    alt={collection.name}
                    className="absolute -bottom-8 left-4 w-16 h-16 rounded-xl border-4 border-dark-800"
                  />
                  {collection.curationScore >= 90 && (
                    <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-medium backdrop-blur-sm">
                      Top Rated
                    </div>
                  )}
                </div>
                <div className="p-4 pt-10">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="font-semibold text-lg group-hover:text-primary-400 transition-colors">
                      {collection.name}
                    </h3>
                    {collection.verified && (
                      <ShieldCheckIcon className="h-5 w-5 text-primary-400" />
                    )}
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-500 mb-4">
                    <span>by {collection.creator.name}</span>
                    {collection.creator.verified && (
                      <SparklesIcon className="h-3 w-3 text-primary-400" />
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Floor</p>
                      <p className="font-semibold">{collection.floorPrice} ETH</p>
                      <p className={`text-xs ${
                        collection.floorChange24h >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {collection.floorChange24h >= 0 ? '+' : ''}{collection.floorChange24h}%
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Volume ({timeRange})</p>
                      <p className="font-semibold">{formatNumber(collection.volume24h)} ETH</p>
                      <p className={`text-xs ${
                        collection.volumeChange24h >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {collection.volumeChange24h >= 0 ? '+' : ''}{collection.volumeChange24h}%
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CollectionsPage;
